"use client";
// beui.dev/components/motion/theme-toggle
// Wired to Bavimail's own theme engine (src/lib/theme.ts: data-theme on <html>,
// persisted preference, system sync) instead of next-themes, and rendered as the
// branded ghost icon Button. The view-transition reveals are beUI's.

import { Moon, Sun } from "lucide-react";
import { useReducedMotion } from "motion/react";
import { useEffect, useSyncExternalStore } from "react";

import { ActionSwapIcon } from "@/components/motion/action-swap";
import { Button, type ButtonProps } from "@/components/motion/button";
import { Tooltip } from "@/components/motion/tooltip";
import { EASE_OUT_CSS } from "@/lib/ease";
import { setTheme, toggleTheme } from "@/lib/theme";
import { cn } from "@/lib/utils";

export type ThemeVariant = "rectangle" | "circle" | "circle-blur" | "blinds";

export type RectStart =
  | "top-left"
  | "top-right"
  | "bottom-left"
  | "bottom-right"
  | "center"
  | "bottom-up";

export interface ThemeToggleProps extends Omit<ButtonProps, "children" | "onClick"> {
  /** View-transition reveal. Default: "circle-blur". */
  reveal?: ThemeVariant;
  /** Origin of the reveal. Default: "top-right", where the toggle sits in every header. */
  start?: RectStart;
  iconClassName?: string;
  /** Tooltip text; pass null to render the bare button. */
  tooltip?: string | null;
}

const VT_STYLE_ID = "beui-theme-toggle-vt";

// View transitions animate in CSS, not motion springs, so easing here is
// either EASE_OUT_CSS or a keyword. Selectors carry [data-theme-transition]
// (set by toggleTheme) so they outrank the plain crossfade in styles.css.
const VT_CSS = `
html[data-theme-transition][data-beui-vt]::view-transition-old(root) {
  animation: none;
  mix-blend-mode: normal;
}
html[data-theme-transition][data-beui-vt="rect"]::view-transition-new(root) {
  mix-blend-mode: normal;
  animation: beui-rect-reveal 400ms ease-out;
}
html[data-theme-transition][data-beui-vt="circle"]::view-transition-new(root) {
  mix-blend-mode: normal;
  animation: beui-circle-reveal 700ms cubic-bezier(0.4, 0, 0.2, 1);
}
html[data-theme-transition][data-beui-vt="circle-blur"]::view-transition-new(root) {
  mix-blend-mode: normal;
  animation: beui-circle-blur-reveal 700ms cubic-bezier(0.4, 0, 0.2, 1);
}
/* Slats: a masked band widens inside every 72px tile. The band edge has to be a
   registered custom property: mask-image itself is not animatable, but it
   re-resolves every frame the property ticks. Without @property support the
   page is revealed in one step. */
@property --beui-vt-slat {
  syntax: "<length>";
  inherits: false;
  initial-value: 72px;
}
html[data-theme-transition][data-beui-vt="blinds"]::view-transition-new(root) {
  mix-blend-mode: normal;
  mask-image: linear-gradient(
    90deg,
    #000 0 var(--beui-vt-slat),
    transparent calc(var(--beui-vt-slat) + 20px)
  );
  mask-size: 72px 100%;
  mask-repeat: repeat;
  animation: beui-blinds-reveal 700ms ${EASE_OUT_CSS};
}
@keyframes beui-rect-reveal {
  from { clip-path: var(--beui-vt-from, inset(100% 0 0 0)); }
  to   { clip-path: inset(0 0 0 0); }
}
@keyframes beui-circle-reveal {
  from { clip-path: circle(0% at var(--beui-vt-origin, 50% 100%)); }
  to   { clip-path: circle(150% at var(--beui-vt-origin, 50% 100%)); }
}
@keyframes beui-circle-blur-reveal {
  from { clip-path: circle(0% at var(--beui-vt-origin, 50% 100%)); filter: blur(8px); }
  to   { clip-path: circle(150% at var(--beui-vt-origin, 50% 100%)); filter: blur(0px); }
}
@keyframes beui-blinds-reveal {
  from { --beui-vt-slat: -20px; }
  to   { --beui-vt-slat: 72px; }
}
`;

const RECT_FROM: Record<RectStart, string> = {
  "top-left": "inset(0 100% 100% 0)",
  "top-right": "inset(0 0 100% 100%)",
  "bottom-left": "inset(100% 100% 0 0)",
  "bottom-right": "inset(100% 0 0 100%)",
  center: "inset(50% 50% 50% 50%)",
  "bottom-up": "inset(100% 0 0 0)",
};

const CIRCLE_ORIGIN: Record<RectStart, string> = {
  "top-left": "0% 0%",
  "top-right": "100% 0%",
  "bottom-left": "0% 100%",
  "bottom-right": "100% 100%",
  center: "50% 50%",
  "bottom-up": "50% 100%",
};

// The resolved theme lives on <html data-theme>, written before paint by
// THEME_INIT_SCRIPT and kept current by startThemeSync().
function subscribeToTheme(onChange: () => void) {
  const observer = new MutationObserver(onChange);
  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ["data-theme"],
  });
  return () => observer.disconnect();
}

const readResolvedTheme = () =>
  document.documentElement.dataset.theme === "light" ? "light" : "dark";
// Unknown on the server: render the placeholder until hydration.
const readServerTheme = () => null;

export function useThemeToggle({
  reveal = "circle-blur",
  start = "top-right",
}: { reveal?: ThemeVariant; start?: RectStart } = {}) {
  const resolvedTheme = useSyncExternalStore(subscribeToTheme, readResolvedTheme, readServerTheme);
  const reduce = useReducedMotion() ?? false;
  const mounted = resolvedTheme !== null;
  const isDark = resolvedTheme === "dark";

  useEffect(() => {
    if (document.getElementById(VT_STYLE_ID)) return;
    const el = document.createElement("style");
    el.id = VT_STYLE_ID;
    el.textContent = VT_CSS;
    document.head.appendChild(el);
  }, []);

  const toggle = () => {
    if (reduce || !("startViewTransition" in document)) {
      setTheme(isDark ? "light" : "dark");
      return;
    }

    const root = document.documentElement;
    if (reveal === "rectangle") {
      root.style.setProperty("--beui-vt-from", RECT_FROM[start]);
      root.dataset.beuiVt = "rect";
    } else if (reveal === "blinds") {
      // Slats sweep the whole viewport; there is no origin point to set.
      root.dataset.beuiVt = "blinds";
    } else {
      root.style.setProperty("--beui-vt-origin", CIRCLE_ORIGIN[start]);
      root.dataset.beuiVt = reveal;
    }

    const finished = toggleTheme();
    if (!finished) {
      delete root.dataset.beuiVt;
      return;
    }
    void finished.finally(() => {
      delete root.dataset.beuiVt;
    });
  };

  return { isDark, mounted, toggle };
}

export function ThemeToggle({
  reveal = "circle-blur",
  start = "top-right",
  className,
  iconClassName,
  tooltip = "Toggle theme",
  variant = "ghost",
  size = "icon",
  ...rest
}: ThemeToggleProps) {
  const { isDark, mounted, toggle } = useThemeToggle({ reveal, start });
  const iconClass = cn("h-5 w-5", iconClassName);

  const button = (
    <Button
      variant={variant}
      size={size}
      aria-label={mounted && isDark ? "Switch to light mode" : "Switch to dark mode"}
      onClick={toggle}
      className={className}
      {...rest}
    >
      {mounted ? (
        <ActionSwapIcon value={isDark ? "dark" : "light"} animation="blur" className={iconClass}>
          {isDark ? <Sun className={iconClass} /> : <Moon className={iconClass} />}
        </ActionSwapIcon>
      ) : (
        <span className={iconClass} aria-hidden="true" />
      )}
    </Button>
  );

  if (tooltip === null) return button;
  return (
    <Tooltip content={tooltip} side="bottom">
      {button}
    </Tooltip>
  );
}
